import {useCallback, useEffect, useRef, useState} from 'react';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$getNearestNodeFromDOMNode, $isTextNode} from 'lexical';
import {collectTextNodes} from '../utils/domText';

const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
function buildRegex(q, matchCase, whole) {
  if (!q) return null;
  const body = whole ? `\\b${esc(q)}\\b` : esc(q);
  return new RegExp(body, matchCase ? 'g' : 'gi');
}

/** Floating Find & Replace. Highlights with the CSS Custom Highlight API (DOM untouched). Matches within a single formatted run. */
export default function FindReplacePlugin({open, disabled, onClose}) {
  const [editor] = useLexicalComposerContext();
  const [q, setQ] = useState('');
  const [rep, setRep] = useState('');
  const [matchCase, setMatchCase] = useState(false);
  const [whole, setWhole] = useState(false);
  const [matches, setMatches] = useState([]);
  const [idx, setIdx] = useState(0);
  const inputRef = useRef(null);

  const scan = useCallback(() => {
    const root = editor.getRootElement();
    const re = buildRegex(q, matchCase, whole);
    if (!root || !re) return [];
    const found = [];
    collectTextNodes(root).forEach((node) => {
      re.lastIndex = 0;
      let m;
      while ((m = re.exec(node.nodeValue))) {
        if (!m[0]) { re.lastIndex += 1; continue; }
        const range = document.createRange();
        range.setStart(node, m.index);
        range.setEnd(node, m.index + m[0].length);
        found.push({node, start: m.index, end: m.index + m[0].length, range});
      }
    });
    return found;
  }, [editor, q, matchCase, whole]);

  const live = open && !disabled;

  useEffect(() => {
    if (!live) { setMatches([]); return undefined; }
    const run = () => setMatches(scan());
    run();
    const off = editor.registerUpdateListener(() => requestAnimationFrame(run));
    return off;
  }, [live, scan, editor]);

  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  const cur = matches.length ? Math.min(idx, matches.length - 1) : 0;

  useEffect(() => {
    if (typeof CSS === 'undefined' || !CSS.highlights || !window.Highlight) return undefined;
    const HighlightCtor = window.Highlight;
    if (!live || !matches.length) {
      CSS.highlights.delete('doc-find');
      CSS.highlights.delete('doc-find-active');
      return undefined;
    }
    CSS.highlights.set('doc-find', new HighlightCtor(...matches.map((m) => m.range)));
    CSS.highlights.set('doc-find-active', new HighlightCtor(matches[cur].range));
    return () => { CSS.highlights.delete('doc-find'); CSS.highlights.delete('doc-find-active'); };
  }, [live, matches, cur]);

  const go = (i) => {
    if (!matches.length) return;
    const n = (i + matches.length) % matches.length;
    setIdx(n);
    const el = matches[n].node.parentElement;
    if (el) el.scrollIntoView({block: 'center'});
  };

  const replaceRange = (list) => {
    editor.update(() => {
      [...list].reverse().forEach((m) => {
        const ln = $getNearestNodeFromDOMNode(m.node);
        if ($isTextNode(ln)) ln.spliceText(m.start, m.end - m.start, rep, false);
      });
    }, {tag: 'history-push'});
  };
  const replaceOne = () => { const list = scan(); if (list.length) replaceRange([list[Math.min(cur, list.length - 1)]]); };
  const replaceAll = () => replaceRange(scan());

  if (!open || disabled) return null;

  return (
    <div className="de-find" onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}>
      <div className="de-find-row">
        <input ref={inputRef} placeholder="Find" value={q} onChange={(e) => { setQ(e.target.value); setIdx(0); }}
          onKeyDown={(e) => { if (e.key === 'Enter') go(cur + (e.shiftKey ? -1 : 1)); }} />
        <span className="de-find-count">{matches.length ? `${cur + 1} / ${matches.length}` : '0 / 0'}</span>
        <button type="button" onClick={() => go(cur - 1)}>Prev</button>
        <button type="button" onClick={() => go(cur + 1)}>Next</button>
        <button type="button" onClick={onClose} aria-label="Close">x</button>
      </div>
      <div className="de-find-row">
        <input placeholder="Replace with" value={rep} onChange={(e) => setRep(e.target.value)} />
        <button type="button" onClick={replaceOne}>Replace</button>
        <button type="button" onClick={replaceAll}>Replace all</button>
      </div>
      <div className="de-find-row">
        <label><input type="checkbox" checked={matchCase} onChange={(e) => setMatchCase(e.target.checked)} /> Match case</label>
        <label><input type="checkbox" checked={whole} onChange={(e) => setWhole(e.target.checked)} /> Whole word</label>
      </div>
    </div>
  );
}